import { all, select, takeEvery, fork, call, put, delay } from 'redux-saga/effects'
import packageJson from '../../../../package.json'

// action
import { customerAuthActions, routeActions } from '@redux/store/index'

// lib
import { persistor } from '@redux/index'
import { Logger } from '@utils'
import { Validator, ValidatorResultType } from '@validators'

// api
import { customerApi, ResType, ParameterType } from '@api'

// schemas
import { Customer, CustomerType } from '@schemas'

// type
import type { CustomerAuthStateType } from '@redux/types'

const purgeStoredState = async (isRedirect: boolean): Promise<void> => {
	await persistor.purge().then(() => {
		if (isRedirect) {
			window.location.reload()
		}
	})
}

const apiJoin = async (data: ParameterType): Promise<ResType | undefined> => {
	const res: ResType | undefined = await customerApi.join(data)
	return res
}

const apiLogin = async (data: ParameterType): Promise<ResType | undefined> => {
	const res: ResType | undefined = await customerApi.login(data)
	return res
}

const apiForgotpassword = async (data: ParameterType): Promise<ResType | undefined> => {
	const res: ResType | undefined = await customerApi.forgotPassword(data)
	return res
}

const apiTokenValidation = async (accessToken: string): Promise<ResType | undefined> => {
	const customHeaders = { Authorization: `Bearer ${accessToken}` }
	const res: ResType | undefined = await customerApi.tokenValidation({
		customHeaders: customHeaders,
	})
	return res
}

const apiUpdateCustomer = async (
	body: {
		id: string
		name: string
		email: string
	},
	accessToken: string,
): Promise<ResType | undefined> => {
	const customHeaders = { Authorization: `Bearer ${accessToken}` }

	const res: ResType | undefined = await customerApi.updateCustomer({
		body: body,
		customHeaders: customHeaders,
	})
	return res
}

const apiUpdateCustomerPassword = async (
	body: {
		id: string
		prevPassword: string
		newPassword: string
	},
	accessToken: string,
): Promise<ResType | undefined> => {
	const customHeaders = { Authorization: `Bearer ${accessToken}` }

	const res: ResType | undefined = await customerApi.updatePassword({
		body: body,
		customHeaders: customHeaders,
	})
	return res
}

const apiSendEmailVerificationCode = async (
	accessToken: string,
): Promise<ResType | undefined> => {
	const customHeaders = { Authorization: `Bearer ${accessToken}` }
	const res: ResType | undefined = await customerApi.sendEmailVerificationCode({
		customHeaders: customHeaders,
	})
	return res
}

const apiVerificationEmail = async (
	accessToken: string,
	body: {
		code: string
	},
): Promise<ResType | undefined> => {
	const customHeaders = { Authorization: `Bearer ${accessToken}` }
	const res: ResType | undefined = await customerApi.verificationEmail({
		customHeaders: customHeaders,
		body: body,
	})
	return res
}

function* loginSaga() {
	try {
		const loginInfo: CustomerAuthStateType['login'] = yield select(
			state => state.customerAuth.login,
		)

		const body = {
			email: loginInfo.email,
			password: loginInfo.password,
		}

		const apiRes: ResType = yield call(apiLogin, { body: { ...body } })

		if (apiRes.status !== 201) {
			const errorMessage = 'Please check your login information again.'
			yield put(customerAuthActions.LOGIN_ERROR_MESSAGE(errorMessage))
		} else {
			const data = apiRes.data[0] as any
			const newCustomer = new Customer({ ...data })
			yield put(customerAuthActions.CUSTOMER(newCustomer))
			yield put(customerAuthActions.IS_LOGEDIN(true))
			yield put(customerAuthActions.LOGIN_ERROR_MESSAGE(''))

			sessionStorage.setItem('accessCustomerToken', newCustomer.accessToken)
			sessionStorage.setItem('refreshCustomerToken', newCustomer.refreshToken)

			yield put(routeActions.NAVIGATE_PATH('/'))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* joinSaga() {
	try {
		const joinInfo: CustomerAuthStateType['join'] = yield select(
			state => state.customerAuth.join,
		)

		const res: ValidatorResultType = Validator.customer({
			name: joinInfo.name,
			email: joinInfo.email,
			password: joinInfo.password,
			confirmPassword: joinInfo.confirmPassword,
		})

		if (!res.name.isValid) {
			yield put(customerAuthActions.JOIN_ERROR_MESSAGE(res.name.message))
		} else if (!res.email.isValid) {
			yield put(customerAuthActions.JOIN_ERROR_MESSAGE(res.email.message))
		} else if (!res.password.isValid) {
			yield put(customerAuthActions.JOIN_ERROR_MESSAGE(res.password.message))
		} else if (!res.confirmPassword.isValid) {
			yield put(customerAuthActions.JOIN_ERROR_MESSAGE(res.confirmPassword.message))
		} else {
			const body = {
				name: joinInfo.name,
				email: joinInfo.email,
				confirmEmailNewsYn: joinInfo.confirmEmailNewsYn ? 1 : 0,
				password: joinInfo.password,
			}

			const apiRes: ResType = yield call(apiJoin, { body: { ...body } })

			if (apiRes.status !== 201) {
				const errorMessage =
					apiRes.exception.message.meta.detail.title ===
					'[CUSTOMER-SERVICE]: Join error - Duplicated email'
						? 'This email has been duplicated.'
						: apiRes.exception.message.meta.detail.title
				yield put(customerAuthActions.JOIN_ERROR_MESSAGE(errorMessage))
			} else {
				const data = apiRes.data[0] as any
				const newCustomer = new Customer({ ...data })
				yield put(customerAuthActions.CUSTOMER(newCustomer))
				yield put(customerAuthActions.IS_LOGEDIN(true))

				sessionStorage.setItem('accessCustomerToken', newCustomer.accessToken)
				sessionStorage.setItem('refreshCustomerToken', newCustomer.refreshToken)

				yield put(routeActions.NAVIGATE_PATH('/'))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* forgotPwSaga() {
	try {
		const forgotPwInfo: CustomerAuthStateType['forgotPassword'] = yield select(
			state => state.customerAuth.forgotPassword,
		)

		if (forgotPwInfo.email !== '') {
			const body = {
				email: forgotPwInfo.email,
			}
			const apiRes: ResType = yield call(apiForgotpassword, { body: { ...body } })

			if (apiRes.status !== 201) {
				const errorMessage =
					apiRes.exception.message.meta.detail.title === '[CUSTOMER-SERVICE]: No Customer'
						? 'There are no customers using this email.'
						: apiRes.exception.message.meta.detail.title

				yield put(customerAuthActions.FORGOT_PASSWORD_ERROR_MESSAGE(errorMessage))
			} else {
				yield put(customerAuthActions.FORGOT_PASSWORD_ERROR_MESSAGE(''))
				yield put(customerAuthActions.SHOW_FORGOT_PASSWORD_MODAL(true))
				yield put(customerAuthActions.FORGOT_PASSWORD({ email: '' }))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerTokenValidationSaga() {
	try {
		const localVersion = localStorage.getItem('systemVersion')
		if (localVersion === null || packageJson.version !== localVersion) {
			localStorage.setItem('systemVersion', packageJson.version)
			yield call(purgeStoredState, true)
		}

		const isLogedin: boolean = yield select(state => state.customerAuth.isLogedin)
		const customer: CustomerType = yield select(state => state.customerAuth.customer)
		const sessionAccessToken = sessionStorage.getItem('accessCustomerToken')

		if (sessionAccessToken === null || sessionAccessToken === '' || !isLogedin) {
			yield put(customerAuthActions.CUSTOMER(new Customer()))
			sessionStorage.setItem('accessCustomerToken', '')
			sessionStorage.setItem('refreshCustomerToken', '')

			yield put(customerAuthActions.IS_LOGEDIN(false))
			yield put(customerAuthActions.IS_LOADING(false))
		} else if ((sessionAccessToken !== null || sessionAccessToken !== '') && isLogedin) {
			const apiRes: ResType = yield call(apiTokenValidation, sessionAccessToken ?? '')

			if (apiRes.status !== 201) {
				if (customer.id !== '') {
					yield put(customerAuthActions.CUSTOMER(new Customer()))
				}

				if (sessionAccessToken !== null || sessionAccessToken !== '') {
					sessionStorage.setItem('accessCustomerToken', '')
					sessionStorage.setItem('refreshCustomerToken', '')
				}

				if (isLogedin) {
					yield put(customerAuthActions.IS_LOGEDIN(false))
				}

				yield put(customerAuthActions.IS_LOADING(false))
			} else {
				const data = apiRes.data[0] as any
				const newCustomer = new Customer({ ...data })

				if (JSON.stringify(newCustomer) !== JSON.stringify(customer)) {
					yield put(customerAuthActions.CUSTOMER(newCustomer))
				}

				if (!isLogedin) {
					yield put(customerAuthActions.IS_LOGEDIN(true))
				}

				yield put(customerAuthActions.IS_LOADING(false))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerLogoutSaga() {
	try {
		sessionStorage.setItem('accessCustomerToken', '')
		sessionStorage.setItem('refreshCustomerToken', '')

		yield put(customerAuthActions.IS_LOGEDIN(false))
		yield call(purgeStoredState, true)
		yield put(customerAuthActions.CUSTOMER(new Customer()))

		yield put(routeActions.NAVIGATE_PATH('/'))
	} catch (error) {
		Logger.warn(error)
	}
}

function* triggerSendEmailVerificationCodeSaga() {
	try {
		const isLogedin: boolean = yield select(state => state.customerAuth.isLogedin)
		const sessionAccessToken = sessionStorage.getItem('accessCustomerToken')

		if (isLogedin && (sessionAccessToken !== null || sessionAccessToken !== '')) {
			const apiRes: ResType = yield call(
				apiSendEmailVerificationCode,
				sessionAccessToken ?? '',
			)

			if (apiRes.status === 201) {
				yield put(
					customerAuthActions.CUSTOMER_INFO_MODAL_INFO({
						target: 'verificationCodeSent',
						title: 'Verification code sent',
					}),
				)
				yield put(customerAuthActions.SHOW_CUSTOMER_INFO_MODAL(true))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* updateCustomerSaga() {
	try {
		const isLogedin: boolean = yield select(state => state.customerAuth.isLogedin)
		const customer: CustomerType = yield select(state => state.customerAuth.customer)
		const updateCustomer: CustomerAuthStateType['updateCustomer'] = yield select(
			state => state.customerAuth.updateCustomer,
		)
		const sessionAccessToken = sessionStorage.getItem('accessCustomerToken')

		if (isLogedin && sessionAccessToken !== null && sessionAccessToken !== '') {
			const body = {
				id: customer.id,
				name: updateCustomer.name,
				email: updateCustomer.email,
				confirmEmailNewsYn: updateCustomer.confirmEmailNewsYn,
			}

			const apiRes: ResType = yield call(apiUpdateCustomer, body, sessionAccessToken)

			if (apiRes.status === 201) {
				// 이메일이 바뀌면 서버가 VERIFIED_YN 을 0 으로 되돌린다. 이 값을 반영하지
				// 않으면 실제로는 미인증인데 화면에 Verified 가 남는다.
				const isEmailChanged: boolean = customer.email !== updateCustomer.email

				yield put(
					customerAuthActions.CUSTOMER({
						...customer,
						...updateCustomer,
						verifiedYn: isEmailChanged ? 0 : customer.verifiedYn,
					}),
				)
				yield put(customerAuthActions.UPDATE_CUSTOMER_ERROR_MESSAGE(''))
				yield put(customerAuthActions.IS_EDIT_CUSTOMER_INFO(false))

				if (isEmailChanged) {
					// 새 주소로 인증 코드를 바로 보낸다 (서버는 DB 의 현재 이메일로 발송한다).
					// Judahs Studio 의 이메일 변경 흐름과 동일하다.
					yield put(customerAuthActions.TRIGGER_SEND_EMAIL_VERIFICATION_CODE(true))
				} else {
					yield put(
						customerAuthActions.CUSTOMER_INFO_MODAL_INFO({
							target: 'updateSuccess',
							title: 'Update Success',
						}),
					)
					yield put(customerAuthActions.SHOW_CUSTOMER_INFO_MODAL(true))
				}
			} else {
				const errorMessage = apiRes.exception.message.meta.detail.title
				yield put(customerAuthActions.UPDATE_CUSTOMER_ERROR_MESSAGE(errorMessage))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* updatePasswordSaga() {
	try {
		const isLogedin: boolean = yield select(state => state.customerAuth.isLogedin)
		const customer: CustomerType = yield select(state => state.customerAuth.customer)
		const updatePassword: CustomerAuthStateType['updatePassword'] = yield select(
			state => state.customerAuth.updatePassword,
		)

		if (
			updatePassword.confirmPassword === '' &&
			updatePassword.newPassword === '' &&
			updatePassword.confirmPassword === ''
		) {
			yield put(customerAuthActions.UPDATE_CUSTOMER_PASSWORD_ERROR_MESSAGE(''))
		} else {
			const res: ValidatorResultType = Validator.customer({
				password: updatePassword.newPassword,
				confirmPassword: updatePassword.confirmPassword,
			})

			if (!res.password.isValid) {
				yield put(
					customerAuthActions.UPDATE_CUSTOMER_PASSWORD_ERROR_MESSAGE(
						res.password.message,
					),
				)
			} else if (!res.confirmPassword.isValid) {
				yield put(
					customerAuthActions.UPDATE_CUSTOMER_PASSWORD_ERROR_MESSAGE(
						res.confirmPassword.message,
					),
				)
			} else {
				const sessionAccessToken = sessionStorage.getItem('accessCustomerToken')

				if (isLogedin && sessionAccessToken !== null && sessionAccessToken !== '') {
					const body = {
						id: customer.id,
						prevPassword: updatePassword.prevPassword,
						newPassword: updatePassword.newPassword,
					}

					const apiRes: ResType = yield call(
						apiUpdateCustomerPassword,
						body,
						sessionAccessToken,
					)

					if (apiRes.status !== 201) {
						const errorMessage =
							apiRes.exception.message.meta.detail.title ===
							'[CUSTOMER-SERVICE]:Wrong Password'
								? 'Current Password is incorrect.'
								: apiRes.exception.message.meta.detail.title

						yield put(
							customerAuthActions.UPDATE_CUSTOMER_PASSWORD_ERROR_MESSAGE(errorMessage),
						)
					} else {
						yield put(
							customerAuthActions.UPDATE_PASSWORD({
								prevPassword: '',
								newPassword: '',
								confirmPassword: '',
							}),
						)
						yield put(customerAuthActions.SHOW_CUSTOMER_PASSWORD_MODAL(true))
					}
				}
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* verificationEmailSaga() {
	try {
		const isLogedin: boolean = yield select(state => state.customerAuth.isLogedin)
		const verificationEmail: CustomerAuthStateType['verificationEmail'] = yield select(
			state => state.customerAuth.verificationEmail,
		)
		const sessionAccessToken = sessionStorage.getItem('accessCustomerToken')

		if (isLogedin && (sessionAccessToken !== null || sessionAccessToken !== '')) {
			const body = {
				code: verificationEmail.code,
			}
			const apiRes: ResType = yield call(
				apiVerificationEmail,
				sessionAccessToken ?? '',
				body,
			)

			if (apiRes.status === 201) {
				yield put(
					customerAuthActions.CUSTOMER_INFO_MODAL_INFO({
						target: 'verificationSuccess',
						title: 'Verification Success',
					}),
				)
				yield put(customerAuthActions.SHOW_CUSTOMER_INFO_MODAL(true))
				yield put(customerAuthActions.TRIGGER_TOKEN_VALIDATION(true))
			} else {
				yield put(
					customerAuthActions.CUSTOMER_INFO_MODAL_INFO({
						target: 'verificationFailed',
						title: 'Verification Failed',
					}),
				)
				yield put(customerAuthActions.SHOW_CUSTOMER_INFO_MODAL(true))
			}
		}
	} catch (error) {
		Logger.warn(error)
	}
}

function* mainSaga() {
	yield takeEvery(
		customerAuthActions.TRIGGER_TOKEN_VALIDATION,
		triggerTokenValidationSaga,
	)
	yield takeEvery(customerAuthActions.TRIGGER_LOGOUT, triggerLogoutSaga)
	yield takeEvery(
		customerAuthActions.TRIGGER_SEND_EMAIL_VERIFICATION_CODE,
		triggerSendEmailVerificationCodeSaga,
	)

	yield takeEvery(customerAuthActions.LOGIN, loginSaga)
	yield takeEvery(customerAuthActions.JOIN, joinSaga)
	yield takeEvery(customerAuthActions.FORGOT_PASSWORD, forgotPwSaga)

	yield takeEvery(customerAuthActions.UPDATE_CUSTOMER, updateCustomerSaga)
	yield takeEvery(customerAuthActions.UPDATE_PASSWORD, updatePasswordSaga)

	yield takeEvery(customerAuthActions.VERIFICATION_EMAIL, verificationEmailSaga)
}

export default function* rootSaga() {
	yield all([fork(mainSaga)])
}
